import Layout from "@/layouts/layout";
import { useMemo, useState } from "react";
import IdvVerificationAlert from "@/components/IdvVerificationAlert";
import PageHeading from "@/components/layout/PageHeading";
import ItemComponent from "@/components/shop/Item";
import ReferralItem from "@/components/shop/ReferralItem";
import { ButtonLink } from "@/components/ui/Button";
import EmptyState from "@/components/ui/EmptyState";
import clsx from "clsx";
import type { Item } from "@/interfaces/item";

const OTHER = "__other__";

function categoryLabel(category: string) {
  if (category === OTHER) return "Other";
  return category.charAt(0).toUpperCase() + category.slice(1);
}

export default function Shop({
  items,
  has_purchased,
  referred_item,
  purchased_item_ids,
  goal_by_item,
}: {
  items: (Item & { stock_left: number })[];
  has_purchased: boolean;
  referred_item: Item | null;
  purchased_item_ids: number[];
  goal_by_item: Record<number, number>;
}) {
  const [selected, setSelected] = useState<string | null>(null);

  // Group items by category, preserving the server's sort order within groups.
  const groups = useMemo(() => {
    const map = new Map<string, (Item & { stock_left: number })[]>();
    for (const item of items) {
      const key = item.category || OTHER;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return map;
  }, [items]);

  // Categories first (alphabetical), with the catch-all "Other" group last.
  const categories = useMemo(
    () =>
      Array.from(groups.keys()).sort((a, b) => {
        if (a === OTHER) return 1;
        if (b === OTHER) return -1;
        return a.localeCompare(b);
      }),
    [groups],
  );

  const hasCategories = categories.length > 1;
  const visible = selected && groups.has(selected) ? [selected] : categories;

  function chipClasses(active: boolean) {
    return clsx(
      "cursor-pointer rounded-full border-2 border-line px-4 py-1.5 font-display text-caption font-bold transition-colors",
      active ? "bg-ink text-white" : "bg-surface text-ink hover:bg-goldenrod",
    );
  }

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          title="Shop"
          subtitle="Spend your hard-earned tickets on cool stuff!"
          actions={
            has_purchased ? (
              <ButtonLink href="/shop/orders" variant="secondary">
                View orders
              </ButtonLink>
            ) : undefined
          }
        />

        <IdvVerificationAlert />

        {referred_item && <ReferralItem item={referred_item} />}

        {items.length === 0 ? (
          <EmptyState
            title="Nothing in the shop yet"
            description="Check back soon - new items get added all the time."
          />
        ) : (
          <>
            {hasCategories && (
              <div className="bg-bg/90 sticky top-14 z-20 -mx-1 flex flex-wrap gap-2 px-1 py-2 backdrop-blur lg:top-0">
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className={chipClasses(selected === null)}
                >
                  All
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setSelected(cat)}
                    className={chipClasses(selected === cat)}
                  >
                    {categoryLabel(cat)}
                    <span className="ml-2 opacity-50">
                      {groups.get(cat)!.length}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-10">
              {visible.map((cat) => (
                <section key={cat} className="flex flex-col gap-4">
                  {hasCategories && selected === null && (
                    <h2 className="font-display text-heading text-ink font-bold">
                      {categoryLabel(cat)}
                    </h2>
                  )}
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {groups.get(cat)!.map((item) => (
                      <ItemComponent
                        key={item.id}
                        item={item}
                        alreadyPurchased={
                          item.one_per_user &&
                          purchased_item_ids.includes(item.id)
                        }
                        goalId={goal_by_item[item.id]}
                        className={
                          item.super_featured ? "sm:col-span-full" : undefined
                        }
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
